import React, { useState, useEffect } from 'react';
import { Container, Table, Badge, Button, Spinner, Modal, Form, Row, Col } from 'react-bootstrap';
import { BoxArrowUpRight, ClipboardData, Download, Pencil } from 'react-bootstrap-icons';
import SurveyService from '../../../services/survey.service';

const timezones = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
    { value: 'UTC', label: 'UTC' }
];

const SuperAdminSurveys = () => {
    const [surveys, setSurveys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [downloadingId, setDownloadingId] = useState(null);

    const [showEditModal, setShowEditModal] = useState(false);
    const [savingEdit, setSavingEdit] = useState(false);
    const [currentSurvey, setCurrentSurvey] = useState(null);
    const [surveyTitle, setSurveyTitle] = useState('');
    const [surveyDescription, setSurveyDescription] = useState('');
    const [surveyActive, setSurveyActive] = useState(true);
    const [surveyCode, setSurveyCode] = useState('');
    const [surveyReportEmails, setSurveyReportEmails] = useState('');
    const [surveyReportFrequency, setSurveyReportFrequency] = useState(1);
    const [surveyReportTimezone, setSurveyReportTimezone] = useState('America/New_York');
    const [surveyStartDate, setSurveyStartDate] = useState('');
    const [surveyEndDate, setSurveyEndDate] = useState('');
    const [surveyProductImageSide, setSurveyProductImageSide] = useState('right');
    const [reportSchedules, setReportSchedules] = useState({});

    useEffect(() => {
        fetchSurveys();
        fetchReportSchedules();
    }, []);

    const fetchReportSchedules = async () => {
        try {
            const response = await SurveyService.getReportSchedules();
            setReportSchedules(response.data || {});
        } catch (err) {
            console.error("Error fetching report schedules:", err);
        }
    };

    const fetchSurveys = async () => {
        try {
            setLoading(true);
            const response = await SurveyService.getAllSurveys();
            console.log("Surveys response:", response);
            setSurveys(response.data || []);
        } catch (err) {
            console.error("Error fetching surveys:", err);
            console.error("Error response:", err.response);
        } finally {
            setLoading(false);
        }
    };

    const getSurveyUrl = (code) => {
        return `${window.location.origin}/survey/${code}`;
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
    };

    const openEditModal = (survey) => {
        setCurrentSurvey(survey);
        setSurveyTitle(survey.title || '');
        setSurveyDescription(survey.description || '');
        setSurveyActive(survey.active !== false);
        setSurveyCode(survey.code || '');
        setSurveyReportEmails(survey.reportEmails || '');
        setSurveyReportFrequency(survey.reportFrequency || 1);
        setSurveyReportTimezone(survey.reportTimezone || 'America/New_York');
        setSurveyStartDate(survey.startDate || '');
        setSurveyEndDate(survey.endDate || '');
        setSurveyProductImageSide(survey.productImageSide === 'left' ? 'left' : 'right');
        setShowEditModal(true);
    };

    const saveSurvey = async () => {
        if (!currentSurvey) return;
        setSavingEdit(true);
        try {
            const data = {
                title: surveyTitle,
                description: surveyDescription,
                active: surveyActive,
                code: surveyCode,
                reportEmails: surveyReportEmails || null,
                reportFrequency: surveyReportFrequency || 1,
                reportTimezone: surveyReportTimezone || 'America/New_York',
                startDate: surveyStartDate || null,
                endDate: surveyEndDate || null,
                productImageSide: surveyProductImageSide === 'left' ? 'left' : 'right'
            };
            await SurveyService.updateSurvey(currentSurvey.id, data);
            setShowEditModal(false);
            fetchSurveys();
        } catch (err) {
            console.error("Error saving survey:", err);
            alert("Error saving survey: " + (err.response?.data?.message || err.message));
        } finally {
            setSavingEdit(false);
        }
    };

    const downloadResponsesExcel = async (survey) => {
        try {
            setDownloadingId(survey.id);
            const response = await SurveyService.downloadResponses(survey.id);
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const today = new Date().toISOString().split('T')[0];
            link.download = `${survey.title.replace(/[^a-zA-Z0-9]/g, '_')}_responses_${today}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Error downloading responses:", err);
            if (err.response?.status === 404) {
                alert(`No responses for "${survey.title}"`);
            } else {
                alert("Failed to download responses for this survey.");
            }
        } finally {
            setDownloadingId(null);
        }
    };

    if (loading) {
        return (
            <Container className="text-center mt-5">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </Container>
        );
    }

    return (
        <Container fluid>
            <h2 className="mb-4">All Surveys</h2>
            <Table striped bordered hover>
                <thead>
                    <tr>
                        <th>Subsite</th>
                        <th>Title</th>
                        <th>Code</th>
                        <th>View</th>
                        <th>Fields</th>
                        <th>Status</th>
                        <th>Edit</th>
                        <th>Results</th>
                    </tr>
                </thead>
                <tbody>
                    {surveys.map(survey => (
                        <tr key={survey.id}>
                            <td>
                                {survey.subsite ? (
                                    <Badge bg="info">{survey.subsite.subsiteName}</Badge>
                                ) : (
                                    <Badge bg="secondary">Unknown</Badge>
                                )}
                            </td>
                            <td>{survey.title}</td>
                            <td>
                                <code>{survey.code}</code>
                                <Button variant="link" size="sm" onClick={() => copyToClipboard(getSurveyUrl(survey.code))}>
                                    <ClipboardData />
                                </Button>
                            </td>
                            <td>
                                <Button variant="link" size="sm" onClick={() => window.open(getSurveyUrl(survey.code), '_blank')}>
                                    <BoxArrowUpRight /> Open
                                </Button>
                            </td>
                            <td>
                                <Badge bg="secondary">{survey.survey_fields?.length || 0}</Badge>
                            </td>
                            <td>
                                <Badge bg={survey.active ? "success" : "secondary"}>
                                    {survey.active ? "Active" : "Inactive"}
                                </Badge>
                            </td>
                            <td>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => openEditModal(survey)}
                                >
                                    <Pencil /> Edit
                                </Button>
                            </td>
                            <td>
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => downloadResponsesExcel(survey)}
                                    disabled={downloadingId === survey.id}
                                >
                                    {downloadingId === survey.id ? (
                                        <Spinner as="span" animation="border" size="sm" />
                                    ) : (
                                        <><Download /> Download</>
                                    )}
                                </Button>
                            </td>
                        </tr>
                    ))}
                    {surveys.length === 0 && (
                        <tr>
                            <td colSpan="8" className="text-center">No surveys found</td>
                        </tr>
                    )}
                </tbody>
            </Table>

            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        Edit Survey
                        {currentSurvey?.subsite && (
                            <Badge bg="info" className="ms-2">{currentSurvey.subsite.subsiteName}</Badge>
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Title</Form.Label>
                        <Form.Control type="text" value={surveyTitle} onChange={(e) => setSurveyTitle(e.target.value)} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Description</Form.Label>
                        <Form.Control as="textarea" rows={3} value={surveyDescription} onChange={(e) => setSurveyDescription(e.target.value)} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Code</Form.Label>
                        <Form.Control
                            type="text"
                            value={surveyCode}
                            onChange={(e) => setSurveyCode(e.target.value.toUpperCase())}
                            maxLength={30}
                            style={{ fontFamily: 'monospace' }}
                        />
                        <Form.Text className="text-muted">
                            Up to 30-character code used in the survey URL
                        </Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Check type="checkbox" label="Active" checked={surveyActive} onChange={(e) => setSurveyActive(e.target.checked)} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Product Preview Side</Form.Label>
                        <Form.Select
                            value={surveyProductImageSide}
                            onChange={(e) => setSurveyProductImageSide(e.target.value)}
                        >
                            <option value="right">Right</option>
                            <option value="left">Left</option>
                        </Form.Select>
                        <Form.Text className="text-muted">
                            Which side the product preview images appear on for respondents (desktop).
                        </Form.Text>
                    </Form.Group>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Start Date</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={surveyStartDate || ''}
                                    onChange={(e) => setSurveyStartDate(e.target.value)}
                                />
                                <Form.Text className="text-muted">
                                    Leave blank for no start bound. Survey opens at the start of this day.
                                </Form.Text>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>End Date</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={surveyEndDate || ''}
                                    onChange={(e) => setSurveyEndDate(e.target.value)}
                                />
                                <Form.Text className="text-muted">
                                    Leave blank for no end bound. Survey closes at the end of this day.
                                </Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Form.Group className="mb-3">
                        <Form.Label>Report Emails</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={surveyReportEmails}
                            onChange={(e) => setSurveyReportEmails(e.target.value)}
                            placeholder="email1@example.com, email2@example.com"
                        />
                        <Form.Text className="text-muted">
                            Comma-separated list of email addresses to receive survey response reports.
                        </Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Report Frequency</Form.Label>
                        <Form.Select
                            value={surveyReportFrequency}
                            onChange={(e) => setSurveyReportFrequency(parseInt(e.target.value))}
                        >
                            <option value={1}>1x per day</option>
                            <option value={2}>2x per day</option>
                            <option value={3}>3x per day</option>
                            <option value={4}>4x per day</option>
                            <option value={5}>5x per day</option>
                            <option value={6}>6x per day</option>
                        </Form.Select>
                        {reportSchedules[surveyReportFrequency] && (
                            <Form.Text className="text-muted">
                                Reports will be sent at: {reportSchedules[surveyReportFrequency].join(', ')}
                            </Form.Text>
                        )}
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Report Timezone</Form.Label>
                        <Form.Select
                            value={surveyReportTimezone}
                            onChange={(e) => setSurveyReportTimezone(e.target.value)}
                        >
                            {timezones.map(tz => (
                                <option key={tz.value} value={tz.value}>{tz.label}</option>
                            ))}
                        </Form.Select>
                        <Form.Text className="text-muted">
                            Report times are based on this timezone.
                        </Form.Text>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditModal(false)} disabled={savingEdit}>Cancel</Button>
                    <Button variant="primary" onClick={saveSurvey} disabled={savingEdit}>
                        {savingEdit ? <Spinner as="span" animation="border" size="sm" /> : 'Save'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default SuperAdminSurveys;

